/**
 * Floating in-app feedback widget.
 * - Visible to authenticated users (always for beta testers; lower friction for paid users too).
 * - Hidden for anonymous + free users.
 * - Click → modal with category radios + 1-5 rating + comment textarea.
 * - Submits to POST /api/feedback.
 */
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquarePlus, X, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuthStore } from "../state/authStore";
import { postJSON } from "../lib/api";

const CATEGORIES = [
    { id: "bug",             label: "Bug",            emoji: "🐛" },
    { id: "feature_request", label: "Feature idea",   emoji: "💡" },
    { id: "ux",              label: "UX / Polish",    emoji: "✨" },
    { id: "other",           label: "Other",          emoji: "💬" },
];

export const FeedbackWidget = () => {
    const { user } = useAuthStore();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [category, setCategory] = useState("bug");
    const [rating, setRating] = useState(4);
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);
    const [viewport, setViewport] = useState("");

    useEffect(() => {
        if (open && typeof window !== "undefined") {
            setViewport(`${window.innerWidth}x${window.innerHeight}`);
        }
    }, [open]);

    // Visibility rules — hide for anonymous + free.
    if (!user) return null;
    const visible = user.is_beta_tester || ["pro", "msp"].includes(user.subscription_plan);
    if (!visible) return null;

    const submit = async () => {
        if (!comment.trim()) {
            toast.error("Please write a comment.");
            return;
        }
        setBusy(true);
        try {
            await postJSON("/feedback", {
                category, rating,
                page: location.pathname,
                comment: comment.trim(),
                viewport,
                user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 240) : "",
            });
            toast.success("Thanks! 🙌 We'll read every word.");
            setOpen(false);
            setComment("");
            setRating(4);
            setCategory("bug");
        } catch (e) {
            toast.error(e?.message || "Could not send feedback");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <button type="button" onClick={() => setOpen(true)} data-testid="feedback-fab"
                className="fixed z-40 bottom-5 right-5 inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-full bg-accent-cyan text-canvas text-[12px] font-medium shadow-glow-cyan hover:scale-[1.02] transition-transform">
                <MessageSquarePlus className="w-3.5 h-3.5" />
                Feedback
                {user.is_beta_tester && (
                    <span className="ml-1 inline-block bg-canvas/20 text-canvas text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded">
                        Beta
                    </span>
                )}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center px-2 sm:px-4"
                        onClick={() => setOpen(false)}>
                        <motion.div
                            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-md bg-surface border border-white/[0.08] rounded-t-2xl sm:rounded-2xl p-5 shadow-heavy-popover" data-testid="feedback-modal">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="font-display font-semibold text-[16px]">Send us feedback</h3>
                                    <p className="mt-0.5 text-[11.5px] text-text-muted">{location.pathname} · {viewport || "—"}</p>
                                </div>
                                <button onClick={() => setOpen(false)} aria-label="Close" data-testid="feedback-close"
                                    className="text-text-dim hover:text-text-primary">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            <p className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim mb-2">Category</p>
                            <div className="grid grid-cols-2 gap-1.5 mb-4">
                                {CATEGORIES.map((c) => (
                                    <button key={c.id} type="button" onClick={() => setCategory(c.id)}
                                        data-testid={`feedback-cat-${c.id}`}
                                        className={["flex items-center gap-2 px-3 py-2 rounded-md border text-[12.5px] text-left transition-colors",
                                            category === c.id
                                                ? "bg-accent-cyan/10 border-accent-cyan/50 text-accent-cyan"
                                                : "bg-canvas border-white/[0.06] text-text-muted hover:border-white/[0.12]",
                                        ].join(" ")}>
                                        <span>{c.emoji}</span> {c.label}
                                    </button>
                                ))}
                            </div>

                            <p className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim mb-2">Rating</p>
                            <div className="flex items-center gap-1 mb-4">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button key={n} type="button" onClick={() => setRating(n)} aria-label={`${n} stars`}
                                        data-testid={`feedback-star-${n}`}
                                        className="p-1.5 transition-transform hover:scale-110">
                                        <Star className={[
                                            "w-5 h-5",
                                            n <= rating ? "fill-accent-amber text-accent-amber" : "text-text-dim",
                                        ].join(" ")} />
                                    </button>
                                ))}
                            </div>

                            <p className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim mb-2">Comment</p>
                            <textarea value={comment} onChange={(e) => setComment(e.target.value.slice(0, 4000))}
                                placeholder="What did you notice? What would you change?"
                                rows={5} data-testid="feedback-comment"
                                className="w-full px-3 py-2 bg-canvas border border-white/[0.08] rounded-md text-[13px] text-text-primary placeholder:text-text-dim focus:outline-none focus:border-accent-cyan" />

                            <div className="mt-4 flex justify-end gap-2">
                                <button onClick={() => setOpen(false)}
                                    className="px-3.5 py-1.5 rounded-md border border-white/[0.08] text-[12.5px] text-text-muted hover:text-text-primary">
                                    Cancel
                                </button>
                                <button onClick={submit} disabled={busy || !comment.trim()}
                                    data-testid="feedback-submit"
                                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-accent-cyan text-canvas text-[12.5px] font-medium hover:shadow-glow-cyan disabled:opacity-50">
                                    {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Sending…</> : "Send feedback"}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default FeedbackWidget;
